import React, { useEffect, useCallback, useMemo, useState, useDeferredValue } from "react";
import {
  Button,
  FormControl,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Select,
  InputLabel,
  IconButton,
  InputAdornment,
  Tooltip,
  CircularProgress,
  Autocomplete,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import UpdateIcon from "@mui/icons-material/Update";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { BooleanQueryInput, BooleanQueryText, BOOLEAN_QUERY_COLORS } from "./BooleanQueryInput";
import { useFilterNotesByTopics } from "../../../../hooks/useFilterNotesByTopics";
import { useGetMediaList } from "../../../../hooks/useGetMediaList";
import { useGetMediaTypes } from "../../../../hooks/useGetMediaTypes";
import { useGetClassification } from "../../../../hooks/useGetClassification";
import { useGetStates } from "../../../../hooks/useGetStates";
import { useSearchThemes } from "../../../../hooks/useSearchThemes";
import { useGetSubThemes } from "../../../../hooks/useGetSubThemes";
import { useDebouncedValue } from "../../../../hooks/useDebouncedValue";
import { useAuth } from "../../../../context/AuthContext";
import { toastAlert } from "../../../../utils/AlertContext";


const OPERADORES_AYUDA = [
  ["&", "and", "Y (AND)"],
  ["|", "or", "O (OR)"],
  ["!", "not", "NO (NOT)"],
  ["( )", "paren", "agrupar"],
  ['"..."', "phrase", "frase exacta"],
];

const EJEMPLOS_CONSULTA = [
  ["presidente & hoy ! embajador | Ronald", "(presidente Y hoy Y NO embajador) O Ronald"],
  ["(presidente | gobernador) & reforma", "presidente o gobernador, y además reforma"],
  ['"Claudia Sheinbaum" & !mañanera', "la frase exacta, sin mencionar mañanera"],
  ["seguridad & (Sinaloa | Sonora | Chihuahua)", "seguridad en cualquiera de esos estados"],
  ["elecciones & !(encuesta | sondeo)", "elecciones, sin encuestas ni sondeos"],
];

const TEMA_QUERY_MIN_LENGTH = 3;
const TEMA_QUERY_DEBOUNCE_MS = 400;

const toIdList = (value) => {
  if (Array.isArray(value)) return value;
  if (value === "" || value === null || value === undefined) return [];
  return [value];
};

const toIdFilter = (value) => {
  const ids = toIdList(value).map(Number).filter((id) => !Number.isNaN(id));
  return ids.length ? ids : "";
};

const MAX_VISIBLE_OPTIONS = 100;

const normalizeText = (text) =>
  String(text ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const MultiSelectFilter = ({
  label,
  options,
  value,
  onChange,
  loading,
  loadingText,
  labelStyle,
  fieldSx,
}) => {
  const [inputValue, setInputValue] = useState("");
  const deferredInput = useDeferredValue(inputValue);

  const searchableOptions = useMemo(
    () =>
      (options || []).map((option) => ({
        option,
        text: normalizeText(option.name),
      })),
    [options],
  );

  const visibleOptions = useMemo(() => {
    const query = normalizeText(deferredInput);
    const result = [];
    for (const { option, text } of searchableOptions) {
      if (!query || text.includes(query)) {
        result.push(option);
        if (result.length === MAX_VISIBLE_OPTIONS) break;
      }
    }
    return result;
  }, [searchableOptions, deferredInput]);

  const selectedOptions = useMemo(() => {
    const selectedIds = new Set(toIdList(value));
    if (selectedIds.size === 0) return [];
    return (options || []).filter((option) => selectedIds.has(option.id));
  }, [options, value]);

  return (
    <>
      <p style={labelStyle}>{label}</p>
      <Autocomplete
        multiple
        fullWidth
        size="small"
        disableCloseOnSelect
        limitTags={2}
        options={visibleOptions}
        filterOptions={(filtered) => filtered}
        inputValue={inputValue}
        onInputChange={(_, newValue, reason) => {
          if (reason !== "reset") setInputValue(newValue);
        }}
        onClose={() => setInputValue("")}
        value={selectedOptions}
        loading={loading}
        disabled={loading}
        getOptionLabel={(option) => option.name ?? ""}
        isOptionEqualToValue={(option, selected) => option.id === selected.id}
        onChange={(_, selected) => onChange(selected.map((option) => option.id))}
        loadingText={loadingText}
        noOptionsText="Sin resultados"
        ChipProps={{ size: "small" }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={selectedOptions.length ? "" : "Buscar..."}
            sx={fieldSx}
          />
        )}
      />
    </>
  );
};

export const TabPanelFilter = ({ fetchNotas, loading, setNotas, setValue, filterState, setFilterState }) => {
  const { token } = useAuth();

  const setField = useCallback((field, value) =>
    setFilterState((prev) => ({ ...prev, [field]: value })),
  [setFilterState]);

  const {
    typeMedio, estado, nombreMedio, clasificacion,
    desdeFecha, hastaFecha, desdeHora, hastaHora,
    modoBusqueda, searchTypeByDate,
    palabraIncluir, palabraExcluir, consultaAvanzada = "",
    modoFormulario, temaQuery,
    temaSeleccionado, subtemaSeleccionado,
  } = filterState;

  const { themes, loadingThemes, errorThemes, searchThemes } = useSearchThemes(token);
  const { subThemes, loadingSubThemes, fetchSubThemes } = useGetSubThemes(token);

  const { mediaList, loading: loadingMediaList } = useGetMediaList(token);
  const { mediaTypes, loading: loadingMediaTypes } = useGetMediaTypes(token);
  const { classifications, loading: loadingClassifications } = useGetClassification(token);
  const { states: statesData, isLoadingStates } = useGetStates();
  const { fetchNotasByTopic, loading: loadingTopic } = useFilterNotesByTopics(token);

  useEffect(() => {
    if (subThemes && subThemes.length > 0 && !subtemaSeleccionado) {
      const primerSubtema = subThemes[0].XID || subThemes[0].id;
      setField("subtemaSeleccionado", primerSubtema);
    }
  }, [subThemes, subtemaSeleccionado, setField]);

  const debouncedTemaQuery = useDebouncedValue(temaQuery, TEMA_QUERY_DEBOUNCE_MS);

  useEffect(() => {
    if (modoFormulario !== "temaQuery") return;

    const trimmedQuery = (debouncedTemaQuery || "").trim();
    if (trimmedQuery.length < TEMA_QUERY_MIN_LENGTH) return;

    searchThemes(trimmedQuery);
    setField("temaSeleccionado", "");
    setField("subtemaSeleccionado", "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedTemaQuery, modoFormulario]);

  const handleAvanzarRango = () => {
    const now = new Date();
    const horaActual = now.toTimeString().slice(0, 5);
    setFilterState((prev) => ({
      ...prev,
      desdeHora: prev.hastaHora,
      hastaHora: horaActual,
    }));
  };

  const cleanObject = (obj) =>
    Object.fromEntries(Object.entries(obj).filter(([_, v]) => v !== ""));

  const clearAdornment = (value, onClear) =>
    value ? (
      <InputAdornment position="end" sx={{ marginRight: "18px" }}>
        <IconButton size="small" onClick={onClear}>
          <ClearIcon fontSize="small" />
        </IconButton>
      </InputAdornment>
    ) : null;

  const buildKeywordFilters = useCallback(() => {
      return cleanObject({
        startDate: desdeFecha,
        endDate: hastaFecha,
        startTime: desdeHora,
        endTime: hastaHora,
        articleKeyword: palabraIncluir,
        wordToExclude: palabraExcluir,
        booleanQuery: consultaAvanzada.trim(),
        searchTypeByDate,
        searchType: modoFormulario,
        stateId: toIdFilter(estado),
        mediaId: toIdFilter(typeMedio),
        mediaNameId: toIdFilter(nombreMedio),
        clasificationId: toIdFilter(clasificacion),
        searchIn: parseInt(modoBusqueda),
      });
    }, [
      desdeFecha,
      hastaFecha,
      desdeHora,
      hastaHora,
      palabraIncluir,
      palabraExcluir,
      consultaAvanzada,
      searchTypeByDate,
      modoFormulario,
      estado,
      typeMedio,
      nombreMedio,
      clasificacion,
      modoBusqueda,
    ]);

  const handleBuscar = async () => {
    if (!token) {
      toastAlert("No hay sesión válida. Por favor, inicia sesión nuevamente.", "error");
      return;
    }

    if (modoFormulario === "temaQuery") {
      if (!temaSeleccionado) {
        toastAlert("Por favor selecciona un tema antes de buscar.", "warning");
        return;
      }
      if (!subtemaSeleccionado) {
        toastAlert("Por favor selecciona un subtema antes de buscar.", "warning");
        return;
      }

      const filtrosTema = cleanObject({
        searchTypeByDate,
        startDate: desdeFecha,
        endDate: hastaFecha,
        startTime: desdeHora,
        endTime: hastaHora,
        topic: Number(temaSeleccionado),
        subtopic: Number(subtemaSeleccionado),
      });

      try {
        const resultados = await fetchNotasByTopic(filtrosTema);
        const palabrasTema = resultados?.topicWords && resultados.topicWords.length > 0
          ? resultados.topicWords
          : (palabraIncluir || "");
        const notasRes = (resultados?.notes || []).map((nota) => ({
          ...nota,
          articleWords: palabrasTema,
        }));
        
        if (setNotas) {
          setNotas({
            notes: notasRes,
            nextCursor: null,
            isNewSearch: true,
            totalNotes: resultados?.data ?? notasRes.length,
            totalMedia: resultados?.totalMedia ?? 0,
          });
        }

        if (setValue) setValue(1);
      } catch (error) {
        console.error("Error en la búsqueda por tema:", error);
        toastAlert(error.message || "Error al realizar la búsqueda por tema.", "error");
      }
      return;
    }

    const filtros = buildKeywordFilters();
    try {
      const resultados = await fetchNotas(filtros, true); // true for new search
      const notasConKeywords = (resultados?.notes || []).map((nota) => ({
        ...nota,
        articleWords: resultados?.keyword || palabraIncluir,
      }));

      if (setNotas) {
        setNotas({
          notes: notasConKeywords,
          nextCursor: resultados.nextCursor,
          isNewSearch: true,
          totalNotes: resultados?.totalNotes ?? notasConKeywords.length,
          totalMedia: resultados?.totalMedia ?? 0,
        });
      }

      if (setValue) setValue(1);
    } catch (error) {
      console.error("Error en la búsqueda:", error);
      if (error.message?.includes("Credenciales incorrectas")) {
        toastAlert("Sesión expirada. Por favor, inicia sesión nuevamente.", "error");
      } else {
        toastAlert(error.message || "Error al realizar la búsqueda.", "error");
      }
    }
  };

  useEffect(() => { setFilterState(prev => ({ ...prev, buildKeywordFilters })) }, [setFilterState, buildKeywordFilters]);
  
  const commonTextFieldStyles = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
    },
  };

  const commonLabelStyle = {
    fontSize: "13px",
    color: "#235789",
    fontWeight: "600",
    margin: "0 0 4px 0",
  };

  const getThemeOptionLabel = (tema) =>
    `${tema.TEXNOMBRE || tema.name || ""} (ID: ${tema.TEXCLAVE || tema.id})`;

  const temaSeleccionadoObj = themes.find(
    (tema) => (tema.TEXCLAVE || tema.id) === temaSeleccionado,
  );
  const temaSeleccionadoLabel = temaSeleccionadoObj
    ? getThemeOptionLabel(temaSeleccionadoObj)
    : temaSeleccionado;

  return (
    <div
      style={{
        height: "100%",
        overflowY: "auto",
        paddingRight: "10px",
        paddingBottom: "10px",
      }}
    >
      <FormControl fullWidth>
        <div style={{ display: "flex", marginBottom: "10px" }}>
          <div style={{ marginRight: "15px", width: "100%" }}>
            <p style={commonLabelStyle}>Desde</p>
            <TextField
              type="date"
              fullWidth
              value={desdeFecha}
              onChange={(e) => setField("desdeFecha", e.target.value)}
              sx={commonTextFieldStyles}
              inputProps={{ max: hastaFecha || undefined }}
            />
          </div>
          <div style={{ width: "100%" }}>
            <p style={commonLabelStyle}>Hasta</p>
            <TextField
              type="date"
              fullWidth
              value={hastaFecha}
              onChange={(e) => setField("hastaFecha", e.target.value)}
              sx={commonTextFieldStyles}
              inputProps={{ min: desdeFecha || undefined }}
            />
          </div>
        </div>

        <div style={{ display: "flex", marginBottom: "10px" }}>
          <div style={{ marginRight: "15px", width: "100%" }}>
            <p style={commonLabelStyle}>Desde</p>
            <TextField
              type="time"
              fullWidth
              value={desdeHora}
              onChange={(e) => setField("desdeHora", e.target.value)}
              sx={commonTextFieldStyles}
            />
          </div>

          <div style={{ width: "100%" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", margin: "0 0 4px 0" }}>
              <p style={{ ...commonLabelStyle, margin: 0 }}>Hasta</p>
              <Tooltip title="Avanzar rango a hora actual">
                <IconButton
                  onClick={handleAvanzarRango}
                  size="small"
                  sx={{
                    color: "#235789",
                    padding: "2px",
                    "&:hover": { backgroundColor: "#e3f0fa" },
                  }}
                >
                  <UpdateIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </div>
            <TextField
              type="time"
              fullWidth
              value={hastaHora}
              onChange={(e) => setField("hastaHora", e.target.value)}
              sx={commonTextFieldStyles}
            />
          </div>
        </div>
      </FormControl>

      <div style={{ display: "flex", gap: 10, marginBottom: 10, justifyContent: "center" }}>
        <Button
          variant={modoFormulario === "palabraClave" ? "contained" : "outlined"}
          color="primary"
          onClick={() => setField("modoFormulario", "palabraClave")}
        >
          Palabra clave
        </Button>
        <Button
          variant={modoFormulario === "temaQuery" ? "contained" : "outlined"}
          color="primary"
          onClick={() => setField("modoFormulario", "temaQuery")}
        >
          Tema (Query)
        </Button>
      </div>

      <div style={{ marginBottom: "8px" }}>
        <p style={commonLabelStyle}>Buscar por fecha de</p>
        <RadioGroup
          value={searchTypeByDate}
          onChange={(e) => setField("searchTypeByDate", e.target.value)}
        >
          <div style={{ display: "flex" }}>
            <FormControlLabel value="captura" control={<Radio size="small" />} label="captura" />
            <FormControlLabel value="publicacion" control={<Radio size="small" />} label="publicación" />
          </div>
        </RadioGroup>
      </div>

      <FormControl fullWidth>
        {modoFormulario === "temaQuery" ? (
          <>
            <div>
              <p style={{ margin: "0 0 4px 0" }}>Buscar tema (Query) por nombre o valor (ID)</p>
              <Autocomplete
                fullWidth
                options={themes}
                getOptionLabel={(tema) => (tema ? getThemeOptionLabel(tema) : "")}
                isOptionEqualToValue={(option, value) =>
                  (option.TEXCLAVE || option.id) === (value?.TEXCLAVE || value?.id)
                }
                filterOptions={(options) => options} // el filtrado ya lo hace el backend
                loading={loadingThemes}
                loadingText="Buscando temas..."
                noOptionsText={
                  temaQuery.trim().length < TEMA_QUERY_MIN_LENGTH
                    ? `Escribe al menos ${TEMA_QUERY_MIN_LENGTH} caracteres para ver coincidencias...`
                    : "No se encontraron temas"
                }
                inputValue={temaQuery}
                onInputChange={(event, newInputValue, reason) => {
                  if (reason === "input") {
                    setField("temaQuery", newInputValue);
                  } else if (reason === "clear") {
                    setField("temaQuery", "");
                    setField("temaSeleccionado", "");
                    setField("subtemaSeleccionado", "");
                  }
                }}
                value={null}
                onChange={(event, newValue) => {
                  if (!newValue) return;
                  const id = newValue.TEXCLAVE || newValue.id;
                  setField("temaSeleccionado", id);
                  setField("subtemaSeleccionado", "");
                  fetchSubThemes(id);
                }}
                renderOption={(props, tema) => (
                  <li {...props} key={tema.TEXCLAVE || tema.id}>
                    {getThemeOptionLabel(tema)}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder='Ejemplo: "política" o "12345"'
                    sx={commonTextFieldStyles}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingThemes ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
              <p style={{ margin: "4px 0 0 0", fontSize: "12px", color: "#888" }}>
                {temaQuery.trim().length > 0 && temaQuery.trim().length < TEMA_QUERY_MIN_LENGTH
                  ? `Escribe al menos ${TEMA_QUERY_MIN_LENGTH} caracteres para ver coincidencias...`
                  : "Haz clic en un resultado de la lista para seleccionarlo."}
              </p>
              {errorThemes && (
                <div style={{ color: "red", margin: "6px 0" }}>{errorThemes}</div>
              )}

              {temaSeleccionado && (
                <>
                  <div style={{ color: "#1976d2", fontWeight: 600, margin: "10px 0 6px" }}>
                    Tema seleccionado: {temaSeleccionadoLabel}
                  </div>

                  <p style={{ margin: "6px 0 4px 0" }}>Lista de subtemas:</p>
                  <FormControl fullWidth sx={{ mb: 1 }}>
                    <InputLabel id="subtema-select-label">Selecciona un subtema</InputLabel>
                    <Select
                      labelId="subtema-select-label"
                      value={subtemaSeleccionado}
                      label="Selecciona un subtema"
                      onChange={(e) => setField("subtemaSeleccionado", e.target.value)}
                      disabled={loadingSubThemes || subThemes.length === 0}
                    >
                      {loadingSubThemes ? (
                        <MenuItem value="" disabled>Cargando...</MenuItem>
                      ) : subThemes.length === 0 ? (
                        <MenuItem value="" disabled>No hay subtemas para este tema</MenuItem>
                      ) : (
                        subThemes.map((subtema) => (
                          <MenuItem key={subtema.XID || subtema.id} value={subtema.XID || subtema.id}>
                            {(subtema.DESCRIPCION || subtema.name) + " (ID: " + (subtema.XID || subtema.id) + ")"}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                    {subtemaSeleccionado && (
                      <div style={{ marginTop: 6, color: "#1976d2", fontWeight: 600 }}>
                        Subtema seleccionado: {subtemaSeleccionado}
                      </div>
                    )}
                  </FormControl>
                </>
              )}
            </div>
          </>
        ) : (
          <>
            <div style={{ marginBottom: "8px" }}>
              <p style={commonLabelStyle}>Tipo de búsqueda</p>
              <RadioGroup
                value={modoBusqueda}
                onChange={(e) => setField("modoBusqueda", e.target.value)}
              >
                <div style={{ display: "flex" }}>
                  <FormControlLabel value="3" control={<Radio size="small" />} label="Ambos" />
                  <FormControlLabel value="2" control={<Radio size="small" />} label="Texto" />
                  <FormControlLabel value="1" control={<Radio size="small" />} label="Título" />
                </div>
              </RadioGroup>
            </div>

            <div style={{ display: "flex", marginBottom: "10px" }}>
              <div style={{ marginRight: "15px", width: "100%" }}>
                <p style={commonLabelStyle}>Incluir palabras</p>
                <TextField
                  fullWidth
                  value={palabraIncluir}
                  onChange={(e) => setField("palabraIncluir", e.target.value)}
                  sx={commonTextFieldStyles}
                />
              </div>
              <div style={{ width: "100%" }}>
                <p style={commonLabelStyle}>Excluir palabras</p>
                <TextField
                  fullWidth
                  value={palabraExcluir}
                  onChange={(e) => setField("palabraExcluir", e.target.value)}
                  sx={commonTextFieldStyles}
                />
              </div>
            </div>

            <div style={{ marginBottom: "10px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 4, margin: "0 0 4px 0" }}>
                <p style={{ ...commonLabelStyle, margin: 0 }}>Consulta avanzada</p>
                <Tooltip
                  placement="bottom-start"
                  componentsProps={{
                    tooltip: {
                      sx: {
                        bgcolor: "#fff",
                        color: "#212121",
                        border: "1px solid #d0dbe6",
                        boxShadow: 3,
                        maxWidth: 380,
                      },
                    },
                  }}
                  title={
                    <div style={{ fontSize: "12px", lineHeight: 1.6 }}>
                      <div style={{ display: "flex", flexWrap: "wrap", columnGap: 12 }}>
                        {OPERADORES_AYUDA.map(([simbolo, kind, texto]) => (
                          <span key={simbolo}>
                            <b style={{ color: BOOLEAN_QUERY_COLORS[kind] }}>{simbolo}</b> {texto}
                          </span>
                        ))}
                      </div>
                      <div style={{ margin: "6px 0 2px", fontWeight: 600 }}>
                        Ejemplos (clic para usar):
                      </div>
                      {EJEMPLOS_CONSULTA.map(([consulta, descripcion]) => (
                        <div
                          key={consulta}
                          onClick={() => setField("consultaAvanzada", consulta)}
                          style={{ cursor: "pointer", padding: "2px 4px", borderRadius: 4 }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = "#eef4fa"; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                        >
                          <code style={{ fontFamily: "monospace" }}>
                            <BooleanQueryText text={consulta} />
                          </code>
                          <div style={{ color: "#666", fontSize: "11px" }}>{descripcion}</div>
                        </div>
                      ))}
                    </div>
                  }
                >
                  <InfoOutlinedIcon fontSize="small" sx={{ color: "#235789", cursor: "help" }} />
                </Tooltip>
              </div>
              <TextField
                fullWidth
                value={consultaAvanzada}
                onChange={(e) => setField("consultaAvanzada", e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !loading) handleBuscar();
                }}
                placeholder='Ej: presidente & hoy ! embajador | Ronald'
                sx={{
                  ...commonTextFieldStyles,
                  "& input::placeholder": { color: "#9e9e9e", opacity: 1 },
                }}
                InputProps={{
                  inputComponent: BooleanQueryInput,
                  endAdornment: clearAdornment(consultaAvanzada, () => setField("consultaAvanzada", "")),
                }}
              />
            </div>

            <div style={{ display: "flex", marginBottom: "10px" }}>
              <div style={{ marginRight: "15px", width: "100%", minWidth: 0 }}>
                <MultiSelectFilter
                  label="Estado o zona"
                  options={statesData}
                  value={estado}
                  onChange={(ids) => setField("estado", ids)}
                  loading={isLoadingStates}
                  loadingText="Cargando estados..."
                  labelStyle={commonLabelStyle}
                  fieldSx={commonTextFieldStyles}
                />
              </div>

              <div style={{ width: "100%", minWidth: 0 }}>
                <MultiSelectFilter
                  label="Clasificación"
                  options={classifications}
                  value={clasificacion}
                  onChange={(ids) => setField("clasificacion", ids)}
                  loading={loadingClassifications}
                  loadingText="Cargando clasificaciones..."
                  labelStyle={commonLabelStyle}
                  fieldSx={commonTextFieldStyles}
                />
              </div>
            </div>

            <div style={{ display: "flex", marginBottom: "10px" }}>
              <div style={{ marginRight: "15px", width: "100%", minWidth: 0 }}>
                <MultiSelectFilter
                  label="Tipo de medio"
                  options={mediaTypes}
                  value={typeMedio}
                  onChange={(ids) => setField("typeMedio", ids)}
                  loading={loadingMediaTypes}
                  loadingText="Cargando tipos de medio..."
                  labelStyle={commonLabelStyle}
                  fieldSx={commonTextFieldStyles}
                />
              </div>

              <div style={{ width: "100%", minWidth: 0 }}>
                <MultiSelectFilter
                  label="Nombre del medio"
                  options={mediaList}
                  value={nombreMedio}
                  onChange={(ids) => setField("nombreMedio", ids)}
                  loading={loadingMediaList}
                  loadingText="Cargando medios..."
                  labelStyle={commonLabelStyle}
                  fieldSx={commonTextFieldStyles}
                />
              </div>
            </div>
          </>
        )}

        <Button
          variant="contained"
          fullWidth
          onClick={handleBuscar}
          style={{
            marginTop: "16px",
            marginBottom: "16px",
            padding: "10px",
            borderRadius: "8px",
          }}
          disabled={loading || loadingTopic}
        >
          {loading || loadingTopic ? "Buscando..." : "Buscar notas"}
        </Button>
      </FormControl>
    </div>
  );
};
